import * as React from 'react';
import { HomeIcon, PanelLeftIcon } from 'lucide-react';
import { cn } from '../lib/utils';
import { useMediaQuery } from '../lib/useMediaQuery';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../components/ui/breadcrumb';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../components/ui/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { activeItemFor, isVisible } from './navItems';
import type { BreadcrumbEntry, NavGroup, NavItem } from './types';
import { useNavPreferences } from './useNavPreferences';

export interface AppShellProps {
  /** Rendered at the top of the sidebar. */
  brand: React.ReactNode;
  /** Shown instead of `brand` when the sidebar is collapsed to icons. */
  brandCollapsed?: React.ReactNode;
  navGroups: NavGroup[];
  /** The current pathname — the active nav entry is derived from it. */
  selectedKey: string;
  onNavigate: (key: string) => void;
  /**
   * Trail after the home icon. Defaults to the active nav entry's label, which
   * is right for every top-level page; detail pages pass their own.
   */
  breadcrumbs?: BreadcrumbEntry[];
  /** App-owned header cluster: search, theme toggle, notifications, user menu. */
  headerRight?: React.ReactNode;
  /** Home route for the breadcrumb's leading icon. */
  homeKey?: string;
  children: React.ReactNode;
}

/**
 * Chrome for every authenticated screen: a sidebar of grouped navigation, a
 * header with the sidebar toggle and breadcrumbs, and the content area.
 *
 * On desktop the sidebar collapses to an icon rail (the preference persists);
 * below `md` it becomes an off-canvas sheet opened from the same toggle.
 */
export function AppShell({
  brand,
  brandCollapsed,
  navGroups,
  selectedKey,
  onNavigate,
  breadcrumbs,
  headerRight,
  homeKey = '/',
  children,
}: AppShellProps) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const showLabels = useNavPreferences((state) => state.showLabels);
  const toggleLabels = useNavPreferences((state) => state.toggleLabels);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const activeItem = React.useMemo(
    () => activeItemFor(navGroups, selectedKey),
    [navGroups, selectedKey],
  );

  const trail: BreadcrumbEntry[] =
    breadcrumbs ?? (activeItem && activeItem.key !== homeKey ? [{ title: activeItem.label }] : []);

  const navigate = (key: string) => {
    setMobileOpen(false);
    onNavigate(key);
  };

  const collapsed = isDesktop && !showLabels;

  return (
    <div className="flex min-h-svh w-full bg-background">
      {/* Desktop sidebar */}
      <aside
        data-collapsed={collapsed || undefined}
        className={cn(
          'sticky top-0 hidden h-svh shrink-0 flex-col border-e border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] duration-200 ease-out md:flex',
          collapsed ? 'w-17' : 'w-64',
        )}
      >
        <SidebarContent
          brand={collapsed ? (brandCollapsed ?? brand) : brand}
          navGroups={navGroups}
          activeKey={activeItem?.key}
          onNavigate={navigate}
          collapsed={collapsed}
        />
      </aside>

      {/* Mobile sidebar */}
      {!isDesktop ? (
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent
            side="left"
            showCloseButton={false}
            className="w-72 border-sidebar-border bg-sidebar p-0 text-sidebar-foreground"
          >
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SheetDescription className="sr-only">Main navigation</SheetDescription>
            <SidebarContent
              brand={brand}
              navGroups={navGroups}
              activeKey={activeItem?.key}
              onNavigate={navigate}
              collapsed={false}
            />
          </SheetContent>
        </Sheet>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-3 border-b border-border/60 bg-card px-4">
          <Button
            variant="ghost"
            size="icon-sm"
            className="-ms-1"
            aria-label={
              isDesktop
                ? showLabels
                  ? 'Collapse navigation'
                  : 'Expand navigation'
                : 'Open navigation'
            }
            onClick={() => (isDesktop ? toggleLabels() : setMobileOpen(true))}
          >
            <PanelLeftIcon className="rtl:rotate-180" />
          </Button>
          <Separator orientation="vertical" className="h-5!" />

          <Breadcrumb className="min-w-0">
            <BreadcrumbList className="flex-nowrap">
              <BreadcrumbItem>
                <button
                  type="button"
                  aria-label="Home"
                  className="inline-flex text-muted-foreground transition-colors hover:text-foreground"
                  onClick={() => onNavigate(homeKey)}
                >
                  <HomeIcon className="size-4" />
                </button>
              </BreadcrumbItem>
              {trail.map((entry, index) => {
                const last = index === trail.length - 1;
                return (
                  <React.Fragment key={entry.key ?? index}>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem className="min-w-0">
                      {last || !entry.key ? (
                        <BreadcrumbPage className="truncate">{entry.title}</BreadcrumbPage>
                      ) : (
                        <button
                          type="button"
                          className="truncate transition-colors hover:text-foreground"
                          onClick={() => entry.key && onNavigate(entry.key)}
                        >
                          {entry.title}
                        </button>
                      )}
                    </BreadcrumbItem>
                  </React.Fragment>
                );
              })}
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex-1" />

          <div className="flex shrink-0 items-center gap-2">{headerRight}</div>
        </header>

        <main className="flex min-w-0 flex-1 flex-col">{children}</main>
      </div>
    </div>
  );
}

function SidebarContent({
  brand,
  navGroups,
  activeKey,
  onNavigate,
  collapsed,
}: {
  brand: React.ReactNode;
  navGroups: NavGroup[];
  activeKey: string | undefined;
  onNavigate: (key: string) => void;
  collapsed: boolean;
}) {
  return (
    <>
      <div
        className={cn(
          'flex shrink-0 items-center py-4',
          collapsed ? 'justify-center px-2' : 'px-4',
        )}
      >
        {brand}
      </div>

      <nav
        aria-label="Main"
        className={cn(
          'flex flex-1 flex-col overflow-y-auto overflow-x-hidden pb-4 [scrollbar-width:none]',
          collapsed ? 'px-2.5' : 'px-2',
        )}
      >
        {navGroups.map((group, index) => {
          const items = group.items.filter(isVisible);
          if (items.length === 0) return null;

          return (
            <div key={index} className={cn(index > 0 && 'mt-4')}>
              {group.title !== undefined && !collapsed ? (
                <div className="px-2 pt-3 pb-2 text-[11px] font-medium tracking-[0.08em] text-sidebar-label uppercase">
                  {group.title}
                </div>
              ) : index > 0 ? (
                <Separator className="mx-auto mb-3 w-8 bg-sidebar-border" />
              ) : null}

              <ul className="grid gap-0.5">
                {items.map((item) => (
                  <NavEntry
                    key={item.key}
                    item={item}
                    activeKey={activeKey}
                    onNavigate={onNavigate}
                    collapsed={collapsed}
                  />
                ))}
              </ul>
            </div>
          );
        })}
      </nav>
    </>
  );
}

function NavEntry({
  item,
  activeKey,
  onNavigate,
  collapsed,
  depth = 0,
}: {
  item: NavItem;
  activeKey: string | undefined;
  onNavigate: (key: string) => void;
  collapsed: boolean;
  depth?: number;
}) {
  const active = item.key === activeKey;
  const children = (item.children ?? []).filter(isVisible);
  const hasBadge = item.badge !== undefined && item.badge > 0;

  const button = (
    <button
      type="button"
      aria-current={active ? 'page' : undefined}
      onClick={() => onNavigate(item.key)}
      className={cn(
        'relative flex h-9 w-full items-center gap-2.5 rounded-lg text-sm transition-colors outline-none',
        'focus-visible:ring-2 focus-visible:ring-sidebar-ring',
        '[&_svg]:size-4 [&_svg]:shrink-0',
        collapsed ? 'justify-center px-0' : 'px-2.5',
        depth > 0 && !collapsed && 'ps-9',
        active
          ? 'bg-sidebar-primary font-medium text-sidebar-primary-foreground'
          : 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
      )}
    >
      {item.icon}
      {collapsed ? (
        <span className="sr-only">{item.label}</span>
      ) : (
        <span className="min-w-0 flex-1 truncate text-start">{item.label}</span>
      )}
      {hasBadge ? (
        <span
          className={cn(
            'flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] leading-none font-semibold text-white tabular-nums',
            collapsed && 'absolute end-1 top-1',
          )}
        >
          {item.badge! > 99 ? '99+' : item.badge}
        </span>
      ) : null}
    </button>
  );

  return (
    <li>
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent side="right">{item.label}</TooltipContent>
        </Tooltip>
      ) : (
        button
      )}

      {children.length > 0 && !collapsed ? (
        <ul className="mt-0.5 grid gap-0.5">
          {children.map((child) => (
            <NavEntry
              key={child.key}
              item={child}
              activeKey={activeKey}
              onNavigate={onNavigate}
              collapsed={collapsed}
              depth={depth + 1}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}
